import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import { AppDispatch } from '../../store';
import { AppState } from '../../store/appState';
import { useOrderLists } from '../common/useOrderLists';
import queryString from 'query-string';
import { useQueryParams } from '../hooks/useQueryParams';
import { ProductSorting } from '../../../models/Product';
import { deserializeFilter } from '../utils/deserializeFilter';
import { DEFAULT_PAGE_SIZE } from '../../yourborrowbox/collection/by-genre/CollectionByGenrePage';
import DocumentTitle from '../components/DocumentTitle';
import { ContainerInnerWithColumns } from '../common/ContainerInner';
import { LoadingWrapper } from '../components/LoadingWrapper';
import LoadingOverlay from '../components/LoadingOverlay';
import BundleDetailHeader from './DetailHeader';
import FilterBar from '../common/FilterBar';
import { FilterAndProductListWrapper } from '../common/FilterAndProductListWrapper';
import ProductList from '../common/ProductList';
import CatalogueListItem from '../../shop/catalogue/CatalogueListItem';
import Pagination, { getPaginationStateObject } from '../common/Pagination';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../components/NoElementsFound';
import { modifyURLState, modifyURLStates } from '../utils/utils-routing';
import { invalidateBundleDetailProducts, useBundleDetailProducts } from '../../api/bundles/useBundleDetailProducts';
import { useBundleDetails } from '../../api/bundles/useBundleDetails';
import { DisplayTypeState } from '../../store/displayType/state';
import { ResponsiveFilterBar } from '../filter/ResponsiveFilterBar';
import { fetchBundleDetailAggregationsQuickSearch } from '../../api/bundles/fetchBundleDetailAggregationsQuickSearch';
import { QueryKeyBuilder } from '../filter/useAggregationsQuery';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { fetchBundleDetailAggregations } from '../../api/bundles/fetchBundleDetailAggregations';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../filter/defaultVisibleFilterGroups';
import { useAddFromBundleToCart } from '../../api/shopping-cart/useAddFromBundleToCart';
import { useAddFromBundleToSelectionList } from '../../api/selection-list/useAddFromBundleToList';
import { useAddToList } from '../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../api/shopping-cart/useAddToCart';
import { CheckBeforeAddingModal } from '../common/CheckBeforeAddingModal';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../common/useListContentChecks';
import { UnavailableProductsModal } from '../common/UnavailableProductsModal';
import { MAX_SELECT, useProductSelection } from '../product-selection/useProductSelection';
import {
    getProductSelectionCount,
    hasProductSelection,
    nothingSelected
} from '../product-selection/ProductSelectionUtils';
import { Orderlist } from '../../../models/OrderList';
import { PriceTier } from '../../../models/PriceTier';
import { SubscriptionPeriodType } from '../../../models/Article';
import { ExcludeProductSelection } from '../product-selection/ProductSelectionTypes';
import { showNotification, TYPE_WARNING } from '../../store/notifications/actions';
import { t } from 'i18next';
import { setActiveList } from '../../store/orderlist/actions';
import BundleCover from './BundleCover';
import BundleBadge from './BundleBadge';
import { LoanFormat, mapLoanFormat } from '../../../models/LoanFormat';
import SelectionBulkContainer from '../../shop/selectionlists/header/SelectionBulkContainer';
import SelectionBulkMenu from '../components/SelectionBulkMenu';

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';

export const BundleDetailPage: FC = () => {
    const { search } = useLocation();
    const history = useHistory();
    const dispatch = useDispatch<AppDispatch>();

    const authentication = useSelector((state: AppState) => state.authentication);
    const { orderlists, createNewList } = useOrderLists();
    const { productSelection, toggleSelectionMode, onSelectButtonClick, selectAll, cancelSelection } =
        useProductSelection();

    const { bundleId } = useParams<{ bundleId: string }>();

    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        display: { value: display }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        display: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const filter = deserializeFilter(filterQueryParam);

    const { data: bundle, isLoading: isLoadingDetails } = useBundleDetails(bundleId);

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(() => defaultVisibleFilterGroups(authentication), [authentication]);
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => {
            return ['bundleDetailPage', bundleId, 'aggregations', filters, expandedFilterGroups];
        },
        [bundleId]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchBundleDetailAggregations({ ...options, bundleId }),
        [bundleId]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options => fetchBundleDetailAggregationsQuickSearch({ ...options, bundleId }),
        [bundleId]
    );
    //endregion

    const { data: bundleProducts, isLoading: isLoadingProducts } = useBundleDetailProducts({
        bundleId,
        pagination: { sortBy, page, pageSize },
        filterMap: filter
    });

    const { isLoading: isLoadingAddProductsToCart, addFromBundleToCartBulk: addBulkToCart } = useAddFromBundleToCart(
        filterQueryParam ?? '',
        () => {
            cancelSelection();
            invalidateBundleDetailProducts(bundleId).then();
        }
    );
    const products = bundleProducts?.hits ?? [];
    const totalProducts = bundleProducts?.totalHits ?? 0;
    const badge = bundle?.salesTag ? <BundleBadge bundle={bundle} /> : null;

    const { isLoading: isLoadingAddProductsToList, addFromBundleToListBulk: addBulkToList } =
        useAddFromBundleToSelectionList(filterQueryParam ?? '', cancelSelection);

    const onBulkAddToCart = async (
        bundleId: string,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: totalProducts
        };

        if (productSelection.totalCount > MAX_SELECT) {
            dispatch(
                showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING)
            );
            throw new Error('max selection exceeded');
        }

        return addBulkToCart(bundleId, priceTier, subscriptionPeriodType, performChecks, productSelection);
    };

    const onBulkAddToList = (
        bundleId: string,
        list: Orderlist,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: totalProducts
        };

        if (productSelection.totalCount > MAX_SELECT) {
            dispatch(
                showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING)
            );
            throw new Error('max selection exceeded');
        }

        return addBulkToList(
            bundleId,
            list.id,
            priceTier,
            subscriptionPeriodType,
            performChecks,
            productSelection
        ).then(data => {
            if (!performChecks) {
                dispatch(setActiveList(list));
            }
            return data;
        });
    };

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: () => invalidateBundleDetailProducts(bundleId).then() });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const productListLength = products.length;

    const loading = isLoadingProducts || isLoadingDetails || isLoadingAddProductsToCart || isLoadingAddProductsToList;

    return (
        <DocumentTitle title={bundle?.title || ''}>
            {loading ? (
                <LoadingWrapper>
                    <LoadingOverlay loading={loading} />
                </LoadingWrapper>
            ) : null}
            <ContainerInnerWithColumns
                className="bndl-detail"
                data-displayname="ContainerInnerWithColumns"
                isLoading={loading}
            >
                <>
                    <SelectionBulkContainer
                        movedUp={hasProductSelection(productSelection)}
                        isSticky={false}
                        bulkMenuComponent={
                            <SelectionBulkMenu
                                selectedCount={getProductSelectionCount(productSelection)}
                                isAddToListEnabled={true}
                                cancelSelection={cancelSelection}
                                onBulkAddToCart={(performChecks, priceTier, subscriptionPeriodType) =>
                                    addBulkToCart(
                                        bundleId,
                                        priceTier,
                                        subscriptionPeriodType,
                                        performChecks,
                                        productSelection
                                    )
                                }
                                onBulkAddToList={(list, performChecks, priceTier, subscriptionPeriodType) =>
                                    addBulkToList(
                                        bundleId,
                                        list.id,
                                        priceTier,
                                        subscriptionPeriodType,
                                        performChecks,
                                        productSelection
                                    )
                                }
                                isNothingSelected={nothingSelected(productSelection)}
                                defaultPriceTier={{ lendingModel: 'OCOU', level: 'MIN' }}
                                showSubscriptionStartDateOption={
                                    bundle?.loanFormat ? mapLoanFormat(bundle.loanFormat) === LoanFormat.ePress : false
                                }
                            />
                        }
                        headerComponent={
                            <BundleDetailHeader
                                bundle={bundle ?? {}}
                                dispatch={dispatch}
                                onAddBundleToCart={(priceTier, subscriptionPeriodType, performChecks) =>
                                    onBulkAddToCart(bundleId, priceTier, subscriptionPeriodType, performChecks)
                                }
                                onAddBundleToList={(list, priceTier, subscriptionPeriodType, performChecks) =>
                                    onBulkAddToList(bundleId, list, priceTier, subscriptionPeriodType, performChecks)
                                }
                                orderlists={orderlists}
                                onCreateNewListClicked={createNewList}
                            />
                        }
                    />

                    <div className="z-index-overwrite">
                        <div className="bndl-cover bndl-cover-detail fl-left">
                            <BundleCover bundle={bundle ?? {}} />
                        </div>
                        <div className="description fl-left">
                            <div>{bundle?.description}</div>
                            {badge}
                        </div>
                    </div>

                    <div className="spacer">
                        <h4>{t('bundles.includedTitles')}</h4>
                    </div>
                    <FilterBar
                        totalProducts={totalProducts}
                        onSortChange={setSortBy}
                        productSelection={productSelection}
                        selectAll={() => selectAll(totalProducts)}
                        toggleSelectionMode={toggleSelectionMode}
                        dispatch={dispatch}
                        currentPage={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        hideGroupBy={true}
                        displayType={display as DisplayTypeState}
                        onFilterDisplayToggle={() => {
                            history.replace({
                                pathname: history.location.pathname,
                                search: modifyURLState(history.location.search, 'displayFilter', !displayFilter, false)
                            });
                        }}
                        onDisplayChange={onDisplayChange}
                        sortBy={sortBy}
                        hideSelectButtons={false}
                        displayFilter={displayFilter}
                    />
                </>
                <FilterAndProductListWrapper>
                    {displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                        />
                    ) : null}
                    {productListLength > 0 || loading ? (
                        <div className="ca-main">
                            <div className="ca-main-inner">
                                <ProductList
                                    orderlists={orderlists}
                                    currentPage={page}
                                    products={products}
                                    onAddPrice={changePrice}
                                    loanFormat={bundle?.loanFormat}
                                    totalProducts={totalProducts}
                                    displayType={display}
                                    listItemComponent={CatalogueListItem}
                                    isProductSelectable={true}
                                    history={history}
                                    productSelection={productSelection}
                                    onSelectProduct={onSelectButtonClick}
                                    useOnlyProductIdForSelectButtonCheck={true}
                                />
                                <Pagination
                                    totalItems={totalProducts}
                                    onPageChange={setPage}
                                    currentPage={page}
                                    pageSize={pageSize}
                                />
                            </div>
                        </div>
                    ) : (
                        <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                    )}
                </FilterAndProductListWrapper>
            </ContainerInnerWithColumns>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </DocumentTitle>
    );
};
